// Effects in the air: mist, rain, snow, fireflies, drifting dust, embers,
// petals, leaves and bubbles. Few of each, moving slowly, with depth: far ones
// small, faint and slow, near ones larger and out of focus. Less of everything
// in the middle of the screen, where the tiles sit.

import { css, layer, noise1, rng, type RGB } from "../scenes";
import { areaOf, calm, clamp01, glowDot, hash2, lift, mixRGB, noiseTexture, smooth, softDot, wrap, type Effect } from "./kit";
import { average, horizon, lumAt } from "./map";

// ---------------------------------------------------------------- mist

interface Bank {
  tex: HTMLCanvasElement;
  /** its middle, 0 to 1 down the screen, and its depth as a share of the screen */
  y: number;
  hgt: number;
  /** pixels a second, on a 1600-wide screen */
  speed: number;
  a: number;
  ph: number;
}

export const mist: Effect = {
  fps: 20,
  res: 0.35,
  blend: "normal",
  build(env) {
    const map = env.map;
    // it hangs a little below where the land meets the sky, unless the theme says where
    const y0 = env.y ?? (map ? Math.min(0.86, Math.max(0.5, horizon(map) + 0.14)) : 0.72);
    // the color of the picture's own light near it, paled; at night, a cold, dim grey-blue
    let col: RGB = env.color ?? (map ? lift(average(map, [0, y0 - 0.3, 1, y0 + 0.1], 0.3, 1) ?? [205, 214, 224], 0.4) : [214, 222, 230]);
    const night = !env.color && map ? map.mean < 0.2 : false;
    if (night) col = mixRGB(col, [110, 128, 156], 0.55);
    const bump = (fy: number) => Math.sin(Math.PI * fy) ** 1.5;
    const bank = (seed: number, cells: number, y: number, hgt: number, speed: number, a: number): Bank => ({
      tex: noiseTexture(512, 96, { seed, cells, rows: 2, octaves: 5, rgb: col, shape: (n, fy) => smooth(0.36, 0.74, n) * bump(fy) }),
      y,
      hgt,
      speed,
      a: a * (night ? 0.7 : 1),
      ph: seed * 1.7,
    });
    const banks = [bank(env.seed * 5 + 1, 4, y0 - 0.12, 0.2, 6, 0.5), bank(env.seed * 5 + 2, 3, y0, 0.26, 10, 0.42), bank(env.seed * 5 + 3, 2, y0 + 0.13, 0.3, 16, 0.32)];
    return { banks };
  },
  frame(ctx, t, state, env) {
    const { banks } = state as { banks: Bank[] };
    const { w, h, u } = env;
    ctx.clearRect(0, 0, w, h);
    const k = 0.35 + env.strength * 0.65;
    for (const b of banks) {
      const tw = w * 1.6;
      const x = -wrap(t * b.speed * u, tw);
      const breath = 0.86 + 0.14 * Math.sin(t * 0.06 + b.ph);
      const top = (b.y - b.hgt / 2 + 0.012 * Math.sin(t * 0.045 + b.ph)) * h;
      ctx.globalAlpha = Math.min(1, b.a * k * breath);
      ctx.drawImage(b.tex, x, top, tw, b.hgt * h);
      ctx.drawImage(b.tex, x + tw, top, tw, b.hgt * h);
    }
    ctx.globalAlpha = 1;
  },
};

// ---------------------------------------------------------------- rain

/** A raindrop's streak, its tail fading up behind it, leaning with the wind. soft draws it out of focus. */
function streak(rgb: RGB, len: number, width: number, lean: number, soft: boolean): HTMLCanvasElement {
  const pad = Math.ceil(width * (soft ? 3 : 1.5)) + 2;
  const dx = Math.sin(lean) * len;
  const cw = Math.ceil(Math.abs(dx) + pad * 2);
  const ch = Math.ceil(len + pad * 2);
  const [c, ctx] = layer(cw, ch);
  const x0 = dx < 0 ? cw - pad : pad;
  const g = ctx.createLinearGradient(x0, pad, x0 + dx, pad + len);
  g.addColorStop(0, css(rgb, 0));
  g.addColorStop(0.7, css(rgb, 0.55));
  g.addColorStop(1, css(rgb, 1));
  ctx.strokeStyle = g;
  ctx.lineCap = "round";
  const passes = soft ? [[width * 2.6, 0.18], [width * 1.6, 0.35], [width, 0.6]] : [[width, 1]];
  for (const [lw, a] of passes) {
    ctx.globalAlpha = a;
    ctx.lineWidth = lw;
    ctx.beginPath();
    ctx.moveTo(x0, pad);
    ctx.lineTo(x0 + dx, pad + len);
    ctx.stroke();
  }
  return c;
}

interface Drop {
  x: number;
  y: number;
  z: number;
  s: number;
  d: number;
}

export const rain: Effect = {
  fps: 30,
  res: 1,
  blend: "normal",
  build(env) {
    const r = rng(env.seed * 17 + 1);
    const { u } = env;
    const lean = 0.1;
    const col = env.color ?? [206, 218, 238];
    // far drops short and fine, near ones long and out of focus, and far fewer of them than there were
    const sprites = [streak(col, 15 * u, 0.8 * u, lean, false), streak(col, 26 * u, 1.1 * u, lean, false), streak(col, 44 * u, 1.8 * u, lean, true)];
    const n = Math.min(260, Math.round((36 + 120 * env.strength) * areaOf(env)));
    const drops: Drop[] = Array.from({ length: n }, () => {
      const z = r() ** 1.4;
      return { x: r() * env.w, y: r() * env.h, z, s: 0.85 + r() * 0.3, d: z < 0.55 ? 0 : z < 0.9 ? 1 : 2 };
    });
    return { drops, sprites, lean: Math.tan(lean) };
  },
  frame(ctx, t, state, env) {
    const { drops, sprites, lean } = state as { drops: Drop[]; sprites: HTMLCanvasElement[]; lean: number };
    const { w, h, u } = env;
    ctx.clearRect(0, 0, w, h);
    const k = 0.55 + env.strength * 0.6;
    const alpha = [0.3, 0.34, 0.14];
    for (const p of drops) {
      const sp = sprites[p.d];
      const v = (700 + 900 * p.z) * u * p.s;
      const y = wrap(p.y + t * v, h + sp.height) - sp.height;
      const x = wrap(p.x + y * lean, w + sp.width) - sp.width / 2;
      ctx.globalAlpha = alpha[p.d] * k * calm(x / w, y / h);
      ctx.drawImage(sp, x, y);
    }
    ctx.globalAlpha = 1;
  },
};

// ---------------------------------------------------------------- snow

interface Flake {
  x: number;
  y: number;
  z: number;
  s: number;
  p: number;
  d: number;
}

export const snow: Effect = {
  fps: 30,
  res: 1,
  blend: "normal",
  build(env) {
    const r = rng(env.seed * 19 + 2);
    const { u } = env;
    const col = env.color ?? [255, 255, 255];
    // far flakes small and nearly sharp, near ones large and out of focus
    const sprites = [softDot(col, 2.1 * u, 0.45), softDot(col, 3.6 * u, 0.32), softDot(col, 11 * u, 0.04)];
    // against a bright sky a falling flake shows grey, as it does in a photograph
    const grey = mixRGB(col, [118, 128, 144], 0.6);
    const backlit = [softDot(grey, 2.1 * u, 0.45), softDot(grey, 3.6 * u, 0.32), softDot(grey, 11 * u, 0.04)];
    const n = Math.min(260, Math.round((32 + 120 * env.strength) * areaOf(env)));
    const flakes: Flake[] = Array.from({ length: n }, () => {
      const z = r() ** 1.6;
      return { x: r() * env.w, y: r() * env.h, z, s: 0.8 + r() * 0.4, p: r() * 6.283, d: z < 0.6 ? 0 : z < 0.9 ? 1 : 2 };
    });
    return { flakes, sprites, backlit };
  },
  frame(ctx, t, state, env) {
    const { flakes, sprites, backlit } = state as { flakes: Flake[]; sprites: HTMLCanvasElement[]; backlit: HTMLCanvasElement[] };
    const { w, h, u } = env;
    ctx.clearRect(0, 0, w, h);
    const k = 0.6 + env.strength * 0.5;
    const alpha = [0.55, 0.8, 0.26];
    for (const f of flakes) {
      let sp = sprites[f.d];
      const v = (14 + 46 * f.z) * u * f.s;
      const y = wrap(f.y + t * v, h + sp.height * 2) - sp.height;
      const x = wrap(f.x + Math.sin(t * (0.22 + 0.25 * f.s) + f.p) * (8 + 26 * f.z) * u + t * 5 * u, w + sp.width) - sp.width / 2;
      if (env.map && lumAt(env.map, x / w, y / h) > 0.66) sp = backlit[f.d];
      ctx.globalAlpha = alpha[f.d] * k * calm(x / w, y / h);
      ctx.drawImage(sp, x - sp.width / 2, y - sp.height / 2);
    }
    ctx.globalAlpha = 1;
  },
};

// ---------------------------------------------------------------- fireflies

interface Fly {
  hx: number;
  hy: number;
  z: number;
  nx: (x: number) => number;
  ny: (x: number) => number;
  period: number;
  ph: number;
  p: number;
}

export const fireflies: Effect = {
  fps: 24,
  res: 1,
  blend: "screen",
  build(env) {
    const r = rng(env.seed * 23 + 3);
    const n = Math.round(8 + 22 * env.strength * Math.min(1.6, areaOf(env)));
    const flies: Fly[] = Array.from({ length: n }, (_, i) => {
      // low down and where the picture is dark, where a small light shows
      let hx = r();
      let hy = 0.55 + r() * 0.4;
      if (env.map) {
        let best = 2;
        for (let c = 0; c < 5; c++) {
          const cx = 0.04 + r() * 0.92;
          const cy = 0.5 + r() * 0.45;
          const l = lumAt(env.map, cx, cy) + (calm(cx, cy) < 0.6 ? 0.2 : 0);
          if (l < best) [best, hx, hy] = [l, cx, cy];
        }
      }
      return { hx, hy, z: r(), nx: noise1(env.seed * 31 + i * 2), ny: noise1(env.seed * 31 + i * 2 + 1), period: 3.2 + r() * 3, ph: r() * 8, p: r() * 100 };
    });
    return { flies, sprite: glowDot(env.color ?? [226, 255, 160], 26 * env.u) };
  },
  frame(ctx, t, state, env) {
    const { flies, sprite } = state as { flies: Fly[]; sprite: HTMLCanvasElement };
    const { w, h, u } = env;
    ctx.clearRect(0, 0, w, h);
    const k = 0.7 + env.strength * 0.4;
    for (const f of flies) {
      // each glows for about two seconds of its cycle and is dark for the rest
      const c = wrap(t + f.ph, f.period);
      const lit = c < 0.6 ? smooth(0, 0.6, c) : c < 1 ? 1 : 1 - smooth(1, 2.4, c);
      if (lit <= 0.01) continue;
      const x = f.hx * w + (f.nx(t * 0.05 + f.p) - 0.5) * 240 * u;
      const y = f.hy * h + (f.ny(t * 0.055 + f.p) - 0.5) * 100 * u;
      const size = sprite.width * (0.5 + 0.7 * f.z);
      ctx.globalAlpha = Math.min(1, lit * (0.55 + 0.45 * f.z) * k);
      ctx.drawImage(sprite, x - size / 2, y - size / 2, size, size);
    }
    ctx.globalAlpha = 1;
  },
};

// ---------------------------------------------------------------- drifting dust

interface Mote {
  x: number;
  y: number;
  z: number;
  p: number;
}

/** The specks of light that drift over the Romantics paintings, kept as they were, since they were liked. */
export const motes: Effect = {
  fps: 24,
  res: 1,
  blend: "normal",
  build(env) {
    const r = rng(env.seed * 13 + 5);
    const n = Math.min(400, Math.round(120 * (0.3 + env.strength * 1.4) * areaOf(env)));
    const list: Mote[] = Array.from({ length: n }, () => ({ x: r() * env.w, y: r() * env.h, z: r() ** 1.7, p: r() * 100 }));
    return { list, sprite: softDot(env.color ?? [255, 242, 214], 4 * env.u) };
  },
  frame(ctx, t, state, env) {
    const { list, sprite } = state as { list: Mote[]; sprite: HTMLCanvasElement };
    const { w, h, u } = env;
    ctx.clearRect(0, 0, w, h);
    const size = sprite.width;
    for (const m of list) {
      const x = wrap(m.x + t * (5 + m.z * 12) * u, w);
      const y = m.y + Math.sin(t * 0.3 + m.p) * 12 * u;
      const k = 0.55 + m.z * 0.9;
      ctx.globalAlpha = 0.12 + 0.4 * Math.max(0, Math.sin(t * 0.8 + m.p)) ** 3;
      ctx.drawImage(sprite, x - (size * k) / 2, y - (size * k) / 2, size * k, size * k);
    }
    ctx.globalAlpha = 1;
  },
};

// ---------------------------------------------------------------- embers

interface Ember {
  i: number;
  z: number;
  life: number;
  ph: number;
  p: number;
  nx: (x: number) => number;
}

export const embers: Effect = {
  fps: 30,
  res: 1,
  blend: "screen",
  build(env) {
    const r = rng(env.seed * 29 + 7);
    // they rise from the fire the theme names, or the warmest light low in the picture, or the whole foot of it
    let src: { x: number; y: number; spread: number } = { x: 0.5, y: 1.02, spread: 0.55 };
    if (env.x !== undefined || env.y !== undefined) src = { x: env.x ?? 0.5, y: env.y ?? 0.85, spread: 0.12 };
    else if (env.map) {
      let best = 0;
      for (let j = Math.floor(env.map.gh * 0.66); j < env.map.gh; j++)
        for (let i = 0; i < env.map.gw; i++) {
          const k = j * env.map.gw + i;
          const warm = (env.map.rgb[k * 3] - env.map.rgb[k * 3 + 2]) / 255;
          const s = env.map.lum[k] * 0.6 + warm;
          // a fire or lava sits low and burns hot; a warm sky behind the tiles is not one
          const y = (j + 0.5) / env.map.gh;
          if (s > best && warm > 0.38 && env.map.lum[k] > 0.42) [best, src] = [s, { x: (i + 0.5) / env.map.gw, y, spread: 0.14 }];
        }
    }
    const n = Math.round(12 + 40 * env.strength);
    const list: Ember[] = Array.from({ length: n }, (_, i) => ({ i, z: r(), life: 5 + r() * 5, ph: r() * 10, p: r() * 100, nx: noise1(env.seed * 37 + i) }));
    return { list, src, sprite: glowDot(env.color ?? [255, 150, 70], 8 * env.u) };
  },
  frame(ctx, t, state, env) {
    const { list, src, sprite } = state as { list: Ember[]; src: { x: number; y: number; spread: number }; sprite: HTMLCanvasElement };
    const { w, h, u } = env;
    ctx.clearRect(0, 0, w, h);
    for (const e of list) {
      const tt = t + e.ph;
      const age = wrap(tt, e.life);
      // a new start each time it comes round, so no two rises are the same
      const round = Math.floor(tt / e.life);
      const x0 = (src.x + (hash2(e.i, round) - 0.5) * 2 * src.spread) * w;
      const y0 = src.y * h;
      const v = (24 + 44 * e.z) * u;
      const y = y0 - age * v;
      const x = x0 + (e.nx(t * 0.25 + e.p) - 0.5) * 70 * u + age * 4 * u;
      const fade = smooth(0, 0.7, age) * (1 - smooth(e.life * 0.45, e.life, age));
      const flicker = 0.84 + 0.16 * Math.sin(t * (4 + e.z * 3) + e.p);
      const a = fade * flicker * (0.55 + 0.45 * e.z) * (0.75 + env.strength * 0.4);
      if (a <= 0.01 || y < -20) continue;
      const size = sprite.width * (0.6 + 0.6 * e.z) * (1 - 0.35 * (age / e.life));
      ctx.globalAlpha = Math.min(1, a);
      ctx.drawImage(sprite, x - size / 2, y - size / 2, size, size);
    }
    ctx.globalAlpha = 1;
  },
};

// ---------------------------------------------------------------- petals and leaves

/** A petal: a soft oval, paler at its heart. */
function petal(rgb: RGB, r: number): HTMLCanvasElement {
  const w = Math.ceil(r * 2.4);
  const h = Math.ceil(r * 1.5);
  const [c, ctx] = layer(w, h);
  const g = ctx.createRadialGradient(w * 0.42, h * 0.45, 0, w / 2, h / 2, r);
  g.addColorStop(0, css(lift(rgb, 0.45), 1));
  g.addColorStop(0.7, css(rgb, 0.95));
  g.addColorStop(1, css(mixRGB(rgb, [120, 60, 80], 0.15), 0.85));
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.ellipse(w / 2, h / 2, r * 1.1, r * 0.62, 0, 0, Math.PI * 2);
  ctx.fill();
  return c;
}

/** A leaf: pointed at both ends, a darker vein down its middle. */
function leaf(rgb: RGB, r: number): HTMLCanvasElement {
  const w = Math.ceil(r * 2.6);
  const h = Math.ceil(r * 1.3);
  const [c, ctx] = layer(w, h);
  const m = h / 2;
  const g = ctx.createLinearGradient(0, 0, 0, h);
  g.addColorStop(0, css(lift(rgb, 0.18), 1));
  g.addColorStop(1, css(mixRGB(rgb, [60, 30, 10], 0.25), 1));
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.moveTo(1, m);
  ctx.quadraticCurveTo(w * 0.45, -h * 0.15, w - 1, m);
  ctx.quadraticCurveTo(w * 0.45, h * 1.15, 1, m);
  ctx.fill();
  ctx.strokeStyle = css(mixRGB(rgb, [50, 25, 10], 0.4), 0.6);
  ctx.lineWidth = Math.max(0.6, r * 0.08);
  ctx.beginPath();
  ctx.moveTo(2, m);
  ctx.lineTo(w - 3, m);
  ctx.stroke();
  return c;
}

interface Tumbler {
  x: number;
  y: number;
  z: number;
  s: number;
  p: number;
  spin: number;
  flip: number;
  look: number;
}

function tumbling(kind: "petals" | "leaves"): Effect {
  return {
    fps: 30,
    res: 1,
    blend: "normal",
    build(env) {
      const r = rng(env.seed * (kind === "petals" ? 41 : 43) + 1);
      const base = env.color ?? (kind === "petals" ? [255, 196, 216] : [214, 132, 62]);
      const looks =
        kind === "petals"
          ? [petal(base, 9 * env.u), petal(lift(base, 0.25), 9 * env.u), petal(mixRGB(base, [255, 255, 255], 0.5), 8 * env.u)]
          : [leaf(base, 12 * env.u), leaf(mixRGB(base, [170, 60, 30], 0.35), 12 * env.u), leaf(mixRGB(base, [200, 170, 60], 0.35), 11 * env.u)];
      const n = Math.round((kind === "petals" ? 4 + 14 * env.strength : 3 + 10 * env.strength) * Math.min(1.6, areaOf(env)));
      const list: Tumbler[] = Array.from({ length: n }, () => ({ x: r() * env.w, y: r() * env.h, z: r() ** 1.3, s: 0.8 + r() * 0.4, p: r() * 6.283, spin: (r() - 0.5) * (kind === "petals" ? 2.2 : 1.4), flip: 0.6 + r() * 1.1, look: Math.floor(r() * 3) }));
      return { list, looks };
    },
    frame(ctx, t, state, env) {
      const { list, looks } = state as { list: Tumbler[]; looks: HTMLCanvasElement[] };
      const { w, h, u } = env;
      ctx.clearRect(0, 0, w, h);
      for (const f of list) {
        const sp = looks[f.look];
        const v = (18 + 26 * f.z) * u * f.s;
        const y = wrap(f.y + t * v, h + sp.width * 4) - sp.width * 2;
        const x = wrap(f.x + t * (14 + 10 * f.z) * u + Math.sin(t * (0.45 + 0.25 * f.s) + f.p) * (30 + 30 * f.z) * u, w + sp.width * 4) - sp.width * 2;
        const near = f.z > 0.86;
        const size = (0.7 + 0.9 * f.z) * (near ? 1.6 : 1);
        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(t * f.spin + f.p);
        // turning over as it falls: narrow edge-on, full face-on
        ctx.scale(size, size * (0.25 + 0.75 * Math.abs(Math.cos(t * f.flip + f.p))));
        ctx.globalAlpha = (near ? 0.42 : 0.88) * calm(x / w, y / h);
        ctx.drawImage(sp, -sp.width / 2, -sp.height / 2);
        ctx.restore();
      }
    },
  };
}

export const petals = tumbling("petals");
export const leaves = tumbling("leaves");

// ---------------------------------------------------------------- bubbles

interface Bubble {
  x: number;
  y: number;
  z: number;
  p: number;
}

export const bubbles: Effect = {
  fps: 24,
  res: 1,
  blend: "screen",
  build(env) {
    const r = rng(env.seed * 47 + 3);
    const n = Math.round((5 + 12 * env.strength) * Math.min(1.6, areaOf(env)));
    const list: Bubble[] = Array.from({ length: n }, () => ({ x: r() * env.w, y: r() * env.h, z: r() ** 1.4, p: r() * 6.283 }));
    return { list, col: env.color ?? ([214, 242, 255] as RGB) };
  },
  frame(ctx, t, state, env) {
    const { list, col } = state as { list: Bubble[]; col: RGB };
    const { w, h, u } = env;
    ctx.clearRect(0, 0, w, h);
    for (const b of list) {
      const rr = (3.5 + 9 * b.z) * u;
      const y = h + rr - wrap(b.y + t * (16 + 30 * b.z) * u, h + rr * 4);
      const x = wrap(b.x + Math.sin(t * 0.7 + b.p) * 10 * u, w);
      const a = (0.25 + 0.3 * b.z) * calm(x / w, y / h) * clamp01(y / (h * 0.4));
      ctx.globalAlpha = a;
      ctx.strokeStyle = css(col, 1);
      ctx.lineWidth = Math.max(0.8, 0.9 * u);
      ctx.beginPath();
      ctx.arc(x, y, rr, 0, Math.PI * 2);
      ctx.stroke();
      ctx.fillStyle = css(lift(col, 0.5), 0.8);
      ctx.beginPath();
      ctx.arc(x - rr * 0.35, y - rr * 0.35, rr * 0.2, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
  },
};
